// Release history shown on the Patch Notes page.
//
// Shape only lives here — version, release date and the *kind* of each change.
// Every user-facing string lives in i18n (en.json / es.json) under
// `patchNotesPage.releases.<release key>`:
//
//   patchNotesPage.releases.<key>.title
//   patchNotesPage.releases.<key>.changes.<change key>
//
// Dates are ISO (YYYY-MM-DD) and are formatted per locale at render time —
// same scope fence as the rest of the app (i18n handles chrome, Intl handles dates).
//
// ADD A NEW RELEASE AT THE TOP OF `patchReleases`, then add the matching
// title/changes keys to BOTH locale files. `patchNotesData.test.ts` fails the
// build if a key is missing from either locale or if the list falls out of order.

export type PatchChangeType = 'feature' | 'improvement' | 'fix';

export interface PatchChange {
  /** Leaf key under `patchNotesPage.releases.<release key>.changes`. */
  key: string;
  type: PatchChangeType;
  /**
   * The staff report this change ships (feedback contract v1.2, C11). `creditName` is the reporter's first name, from the
   * report's "Reported by" (later core's `/shipped` answer); `null` when they declined credit. With a name, the line ends
   * `Suggested by <name>` / `Sugerido por <name>` in both locales; without one, it carries no name.
   * `patchNotesData.test.ts` pins both.
   */
  feedback?: { reference: string; creditName: string | null };
}

export interface PatchRelease {
  /** Semver label shown as `v1.2.3`. */
  version: string;
  /** ISO date (YYYY-MM-DD) the release reached staff. */
  date: string;
  /** Group key under `patchNotesPage.releases`. */
  key: string;
  changes: PatchChange[];
}

/** Newest first. */
export const patchReleases: PatchRelease[] = [
  {
    version: '1.53.0',
    date: '2026-11-03',
    key: 'lookupFilters',
    changes: [
      { key: 'pickSeveral', type: 'feature' },
      { key: 'excludeAndNoValue', type: 'feature' },
    ],
  },
  {
    version: '1.52.1',
    date: '2026-11-02',
    key: 'formChangedHistory',
    changes: [
      { key: 'formChangeRecorded', type: 'improvement' },
    ],
  },
  {
    version: '1.52.0',
    date: '2026-11-02',
    key: 'clientQuickViews',
    changes: [
      { key: 'clientViewsRow', type: 'feature' },
    ],
  },
  {
    version: '1.51.0',
    date: '2026-11-02',
    key: 'quickViews',
    changes: [
      { key: 'viewsRow', type: 'feature' },
      { key: 'ownerNamed', type: 'improvement' },
    ],
  },
  {
    version: '1.50.2',
    date: '2026-10-29',
    key: 'noUploadAgain',
    changes: [
      { key: 'draftNotRebuilt', type: 'fix' },
    ],
  },
  {
    version: '1.50.1',
    date: '2026-10-29',
    key: 'rateStopInYourLanguage',
    changes: [
      { key: 'removeNeedsRate', type: 'fix' },
    ],
  },
  {
    version: '1.50.0',
    date: '2026-10-29',
    key: 'realLinks',
    changes: [
      // Fleet ruling 2026-10-29 from Helen's feedback; credited per feedback contract v1.2 (C11).
      { key: 'openInNewTab', type: 'improvement', feedback: { reference: 'fleet real links (Helen, 2026-10-29)', creditName: 'Helen' } },
    ],
  },
  {
    version: '1.49.5',
    date: '2026-10-29',
    key: 'floridaOnlyDrafts',
    changes: [
      { key: 'noFloridaDraftElsewhere', type: 'fix' },
      { key: 'formNotConfigured', type: 'improvement' },
    ],
  },
  {
    version: '1.49.4',
    date: '2026-10-28',
    key: 'fleetTypeface',
    changes: [
      { key: 'systemFont', type: 'improvement' },
    ],
  },
  {
    version: '1.49.3',
    date: '2026-10-28',
    key: 'datesInYourLanguage',
    changes: [
      { key: 'everyDate', type: 'fix' },
    ],
  },
  {
    version: '1.49.2',
    date: '2026-10-28',
    key: 'frequencyEverywhere',
    changes: [
      { key: 'everyFrequency', type: 'fix' },
      { key: 'lastFiledInSpanish', type: 'fix' },
    ],
  },
  {
    version: '1.49.1',
    date: '2026-10-28',
    key: 'polishFollowUp',
    changes: [
      { key: 'notesFit', type: 'fix' },
      { key: 'frequencyInSpanish', type: 'fix' },
      { key: 'taxNameOnly', type: 'improvement' },
    ],
  },
  {
    version: '1.49.0',
    date: '2026-10-28',
    key: 'polishPass',
    changes: [
      { key: 'fullWidth', type: 'improvement' },
      { key: 'inWords', type: 'improvement' },
      { key: 'overdueEverywhere', type: 'fix' },
      { key: 'emptySays', type: 'improvement' },
      { key: 'consistentLook', type: 'improvement' },
    ],
  },
  {
    version: '1.48.1',
    date: '2026-10-28',
    key: 'wholeDayDates',
    changes: [
      { key: 'endDateDay', type: 'fix' },
      { key: 'notOwedAfterEnd', type: 'fix' },
      { key: 'datesSwept', type: 'fix' },
    ],
  },
  {
    version: '1.48.0',
    date: '2026-10-28',
    key: 'dropEverywhere',
    changes: [
      { key: 'manualFilingDrop', type: 'feature' },
      { key: 'twoAtOnce', type: 'improvement' },
      { key: 'refusedUpFront', type: 'improvement' },
    ],
  },
  {
    version: '1.47.1',
    date: '2026-10-28',
    key: 'smsDateRange',
    changes: [
      { key: 'smsHyphen', type: 'improvement' },
    ],
  },
  {
    version: '1.47.0',
    date: '2026-10-28',
    key: 'clientLanguagePreview',
    changes: [
      { key: 'previewLanguage', type: 'improvement' },
    ],
  },
  {
    version: '1.46.0',
    date: '2026-10-27',
    key: 'calendarPicker',
    changes: [
      { key: 'monthArrows', type: 'fix' },
      { key: 'feedbackLanguage', type: 'improvement' },
    ],
  },
  {
    version: '1.45.0',
    date: '2026-10-27',
    key: 'sweepDryRunButton',
    changes: [
      { key: 'dryRun', type: 'feature' },
    ],
  },
  {
    version: '1.44.0',
    date: '2026-10-26',
    key: 'needsAttentionLifecycle',
    changes: [
      { key: 'endedOrDeactivated', type: 'fix' },
      { key: 'automationRule', type: 'improvement' },
    ],
  },
  {
    version: '1.43.0',
    date: '2026-10-26',
    key: 'estimateNotFiled',
    changes: [
      { key: 'reconcileStartsEmpty', type: 'fix' },
      { key: 'navLabel', type: 'improvement' },
    ],
  },
  {
    version: '1.42.0',
    date: '2026-10-22',
    key: 'automationReadiness',
    changes: [
      { key: 'readiness', type: 'feature' },
    ],
  },
  {
    version: '1.41.0',
    date: '2026-10-22',
    key: 'feedbackWidget',
    changes: [
      { key: 'feedback', type: 'feature' },
    ],
  },
  {
    version: '1.40.0',
    date: '2026-10-21',
    key: 'spanishSlice3',
    changes: [
      { key: 'slice3', type: 'improvement' },
    ],
  },
  {
    version: '1.39.0',
    date: '2026-10-21',
    key: 'spanishSlice2',
    changes: [
      { key: 'slice2', type: 'improvement' },
    ],
  },
  {
    version: '1.38.0',
    date: '2026-10-21',
    key: 'spanishSlice1',
    changes: [
      { key: 'slice1', type: 'improvement' },
    ],
  },
  {
    version: '1.37.0',
    date: '2026-10-21',
    key: 'firstFiling',
    changes: [
      { key: 'nod', type: 'feature' },
    ],
  },
  {
    version: '1.36.0',
    date: '2026-10-21',
    key: 'silentSignIn',
    changes: [
      { key: 'silent', type: 'improvement' },
    ],
  },
  {
    version: '1.35.0',
    date: '2026-10-21',
    key: 'languageFollows',
    changes: [
      { key: 'perPerson', type: 'improvement' },
    ],
  },
  {
    version: '1.34.0',
    date: '2026-10-21',
    key: 'recalculateDraft',
    changes: [
      { key: 'oneTask', type: 'improvement' },
    ],
  },
  {
    version: '1.33.0',
    date: '2026-10-21',
    key: 'figuresError',
    changes: [
      { key: 'say', type: 'fix' },
    ],
  },
  {
    version: '1.32.0',
    date: '2026-10-21',
    key: 'streak',
    changes: [
      { key: 'onTime', type: 'feature' },
    ],
  },
  {
    version: '1.31.0',
    date: '2026-10-21',
    key: 'sameSales',
    changes: [
      { key: 'warn', type: 'improvement' },
    ],
  },
  {
    version: '1.30.0',
    date: '2026-10-21',
    key: 'oneFileDrop',
    changes: [
      { key: 'refuse', type: 'fix' },
    ],
  },
  {
    version: '1.29.0',
    date: '2026-10-21',
    key: 'usage',
    changes: [
      { key: 'weekly', type: 'feature' },
    ],
  },
  {
    version: '1.28.0',
    date: '2026-10-21',
    key: 'quickStart',
    changes: [
      { key: 'guide', type: 'feature' },
    ],
  },
  {
    version: '1.27.0',
    date: '2026-10-21',
    key: 'syncFreshness',
    changes: [
      { key: 'crmSync', type: 'feature' },
    ],
  },
  {
    version: '1.26.0',
    date: '2026-10-21',
    key: 'moments',
    changes: [
      { key: 'myPeriod', type: 'feature' },
      { key: 'hooray', type: 'feature' },
      { key: 'offSwitch', type: 'feature' },
    ],
  },
  {
    version: '1.25.0',
    date: '2026-10-20',
    key: 'rateSavedComputes',
    changes: [
      { key: 'computeOnSave', type: 'improvement' },
      { key: 'existingDraftKept', type: 'improvement' },
    ],
  },
  {
    version: '1.24.0',
    date: '2026-10-20',
    key: 'filterApply',
    changes: [
      { key: 'applyClear', type: 'improvement' },
      { key: 'onePanel', type: 'improvement' },
      { key: 'easternToday', type: 'fix' },
      { key: 'emptyExtraction', type: 'fix' },
    ],
  },
  {
    version: '1.23.0',
    date: '2026-10-20',
    key: 'countySurtax',
    changes: [
      { key: 'surtaxVisible', type: 'feature' },
      { key: 'surtaxSplit', type: 'feature' },
      { key: 'periodRate', type: 'improvement' },
      { key: 'percentEntry', type: 'fix' },
    ],
  },
  {
    version: '1.22.0',
    date: '2026-08-06',
    key: 'julyRollout',
    changes: [
      { key: 'obligationsSeeded', type: 'feature' },
      { key: 'crmStatusInbound', type: 'feature' },
      { key: 'julySlates', type: 'feature' },
      { key: 'completedNotDuplicated', type: 'improvement' },
      { key: 'pastMonths', type: 'improvement' },
    ],
  },
  {
    version: '1.21.0',
    date: '2026-08-06',
    key: 'accuracyPass',
    changes: [
      { key: 'recomputeOnRemoval', type: 'fix' },
      { key: 'suppressedSend', type: 'fix' },
      { key: 'perFilingGuard', type: 'fix' },
      { key: 'fleetRollback', type: 'fix' },
      { key: 'seedPreview', type: 'feature' },
    ],
  },
  {
    version: '1.20.0',
    date: '2026-08-06',
    key: 'obligationSeeding',
    changes: [
      { key: 'panel', type: 'feature' },
      { key: 'dryRun', type: 'feature' },
      { key: 'generationSwitch', type: 'feature' },
    ],
  },
  {
    version: '1.19.1',
    date: '2026-08-05',
    key: 'periodDueStatus',
    changes: [
      { key: 'dueNow', type: 'fix' },
    ],
  },
  {
    version: '1.19.0',
    date: '2026-08-05',
    key: 'filingEvidence',
    changes: [
      { key: 'openTaskNotFiled', type: 'fix' },
      { key: 'activityFeed', type: 'fix' },
    ],
  },
  {
    version: '1.18.0',
    date: '2026-07-24',
    key: 'darkMode',
    changes: [
      { key: 'theme', type: 'feature' },
      { key: 'toggle', type: 'feature' },
      { key: 'patchNotes', type: 'feature' },
    ],
  },
  {
    version: '1.17.0',
    date: '2026-07-21',
    key: 'frequencySegments',
    changes: [
      { key: 'segments', type: 'improvement' },
      { key: 'openChip', type: 'improvement' },
      { key: 'testCompany', type: 'improvement' },
      { key: 'freqLogDedupe', type: 'fix' },
    ],
  },
  {
    version: '1.16.0',
    date: '2026-07-18',
    key: 'filteringAndCatchUp',
    changes: [
      { key: 'headerFilters', type: 'feature' },
      { key: 'filterParity', type: 'improvement' },
      { key: 'catchUp', type: 'feature' },
      { key: 'spanishReview', type: 'improvement' },
    ],
  },
  {
    version: '1.15.0',
    date: '2026-07-15',
    key: 'obligationLifecycle',
    changes: [
      { key: 'endObligation', type: 'feature' },
      { key: 'reactivate', type: 'feature' },
      { key: 'activeCount', type: 'fix' },
    ],
  },
  {
    version: '1.14.0',
    date: '2026-07-14',
    key: 'nameGuard',
    changes: [
      { key: 'nameMatch', type: 'feature' },
      { key: 'override', type: 'feature' },
      { key: 'dbaOnSummary', type: 'improvement' },
      { key: 'removeObligation', type: 'feature' },
      { key: 'duplicateFilings', type: 'fix' },
    ],
  },
  {
    version: '1.13.0',
    date: '2026-07-08',
    key: 'generationControl',
    changes: [
      { key: 'preview', type: 'feature' },
      { key: 'obligationPicker', type: 'feature' },
      { key: 'cancelReopen', type: 'feature' },
      { key: 'previewNotFound', type: 'fix' },
    ],
  },
  {
    version: '1.12.0',
    date: '2026-07-04',
    key: 'sourcesAndJournal',
    changes: [
      { key: 'figureMatrix', type: 'feature' },
      { key: 'xeroJournal', type: 'feature' },
      { key: 'testClientExclusion', type: 'improvement' },
    ],
  },
  {
    version: '1.11.0',
    date: '2026-07-03',
    key: 'conversionDashboard',
    changes: [
      { key: 'fullRollout', type: 'feature' },
      { key: 'conversionSection', type: 'feature' },
      { key: 'perFigure', type: 'feature' },
      { key: 'reviewLog', type: 'feature' },
      { key: 'navPlacement', type: 'improvement' },
      { key: 'notifiedStamp', type: 'fix' },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-07-02',
    key: 'sourceIntake',
    changes: [
      { key: 'multiFormat', type: 'feature' },
      { key: 'dragDrop', type: 'feature' },
      { key: 'removeSource', type: 'fix' },
      { key: 'surtaxLine', type: 'improvement' },
      { key: 'taxableAnchor', type: 'improvement' },
      { key: 'saveFiledAmounts', type: 'fix' },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-06-23',
    key: 'historyReopen',
    changes: [
      { key: 'reopenFromHistory', type: 'feature' },
      { key: 'versionHistory', type: 'feature' },
      { key: 'accuracyCard', type: 'feature' },
      { key: 'smsStop', type: 'feature' },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-06-22',
    key: 'emailsAndSources',
    changes: [
      { key: 'signature', type: 'feature' },
      { key: 'attachments', type: 'feature' },
      { key: 'attachmentPicker', type: 'feature' },
      { key: 'debitDate', type: 'improvement' },
      { key: 'sourcesAllTypes', type: 'feature' },
      { key: 'threePosSlots', type: 'feature' },
      { key: 'onFileIndicator', type: 'improvement' },
      { key: 'savedViews', type: 'feature' },
      { key: 'multiSelectSources', type: 'feature' },
      { key: 'actionsMenu', type: 'improvement' },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-06-21',
    key: 'threePhase',
    changes: [
      { key: 'accordion', type: 'feature' },
      { key: 'reconcileEditor', type: 'feature' },
      { key: 'finalizeLock', type: 'improvement' },
      { key: 'reopenAmend', type: 'feature' },
      { key: 'surtaxCorrection', type: 'fix' },
      { key: 'surtaxAllCounties', type: 'improvement' },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-06-19',
    key: 'clientUpdates',
    changes: [
      { key: 'manualSend', type: 'feature' },
      { key: 'previewBeforeSend', type: 'feature' },
      { key: 'sentIndicator', type: 'improvement' },
      { key: 'noAutoSend', type: 'improvement' },
      { key: 'testRecipient', type: 'feature' },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-06-17',
    key: 'computedDrafts',
    changes: [
      { key: 'computeDraft', type: 'feature' },
      { key: 'rateConfig', type: 'feature' },
      { key: 'useDraft', type: 'feature' },
      { key: 'surtaxTable', type: 'feature' },
      { key: 'rateHardStop', type: 'improvement' },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-06-16',
    key: 'actionableHistory',
    changes: [
      { key: 'stripActions', type: 'feature' },
      { key: 'unfile', type: 'feature' },
      { key: 'extractPrefill', type: 'feature' },
      { key: 'taskFilters', type: 'feature' },
      { key: 'customDueDate', type: 'feature' },
      { key: 'twoSlotUpload', type: 'feature' },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-06-10',
    key: 'documentExtraction',
    changes: [
      { key: 'uploadExtract', type: 'feature' },
      { key: 'crossCheck', type: 'improvement' },
      { key: 'markFiledPrefill', type: 'improvement' },
      { key: 'selfPay', type: 'feature' },
      { key: 'formAware', type: 'feature' },
      { key: 'scheduledPayment', type: 'improvement' },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-05-31',
    key: 'filingDetail',
    changes: [
      { key: 'filingDetailModal', type: 'feature' },
      { key: 'manualFiling', type: 'feature' },
      { key: 'quarterGrid', type: 'feature' },
      { key: 'cadenceMismatch', type: 'feature' },
      { key: 'effectiveDatedFrequency', type: 'feature' },
      { key: 'bulkEdit', type: 'feature' },
      { key: 'staffRoles', type: 'feature' },
      { key: 'billingStatus', type: 'feature' },
      { key: 'secureSignIn', type: 'improvement' },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-05-05',
    key: 'filingHealth',
    changes: [
      { key: 'historyStrip', type: 'feature' },
      { key: 'triage', type: 'feature' },
      { key: 'needsAttention', type: 'feature' },
      { key: 'snooze', type: 'feature' },
      { key: 'notes', type: 'feature' },
      { key: 'activityFeed', type: 'feature' },
      { key: 'lifecycleHistory', type: 'feature' },
      { key: 'communications', type: 'feature' },
      { key: 'nextDue', type: 'feature' },
      { key: 'bookkeepingChip', type: 'feature' },
      { key: 'emailAttachments', type: 'improvement' },
      { key: 'smsStopNotice', type: 'improvement' },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-04-22',
    key: 'initialRelease',
    changes: [
      { key: 'clients', type: 'feature' },
      { key: 'obligations', type: 'feature' },
      { key: 'taskGeneration', type: 'feature' },
      { key: 'taskList', type: 'feature' },
      { key: 'dashboard', type: 'feature' },
      { key: 'notifications', type: 'feature' },
      { key: 'attachments', type: 'feature' },
      { key: 'bilingual', type: 'feature' },
      { key: 'pilot', type: 'improvement' },
    ],
  },
];
